// Per-product artwork for the service cards (All Services + Security landing).
// These are the design-system's product glyphs (icons/svc/*.svg) — distinct from
// the generic category icons — keyed by provider scope, with a dimmed `-soon`
// variant for coming-soon products. Imported as URLs (rspack asset/resource).

import fastchannel from '@/assets/services/svc/fastchannel.svg';
import fastchannelSoon from '@/assets/services/svc/fastchannel-soon.svg';
import livestream from '@/assets/services/svc/livestream.svg';
import livestreamSoon from '@/assets/services/svc/livestream-soon.svg';
import mediaService from '@/assets/services/svc/media-service.svg';
import mediaServiceSoon from '@/assets/services/svc/media-service-soon.svg';
import transcoding from '@/assets/services/svc/transcoding.svg';
import transcodingSoon from '@/assets/services/svc/transcoding-soon.svg';
import aesGateway from '@/assets/services/svc/aes-gateway.svg';
import aesGatewaySoon from '@/assets/services/svc/aes-gateway-soon.svg';
import multiCdn from '@/assets/services/svc/multi-cdn.svg';
import multiCdnSoon from '@/assets/services/svc/multi-cdn-soon.svg';
import cloudCompute from '@/assets/services/svc/cloud-compute.svg';
import cloudComputeSoon from '@/assets/services/svc/cloud-compute-soon.svg';
import enterpriseCompute from '@/assets/services/svc/enterprise-compute.svg';
import enterpriseComputeSoon from '@/assets/services/svc/enterprise-compute-soon.svg';
import serverless from '@/assets/services/svc/serverless.svg';
import serverlessSoon from '@/assets/services/svc/serverless-soon.svg';
import containerRegistry from '@/assets/services/svc/container-registry.svg';
import containerRegistrySoon from '@/assets/services/svc/container-registry-soon.svg';
import kubernetes from '@/assets/services/svc/kubernetes.svg';
import kubernetesSoon from '@/assets/services/svc/kubernetes-soon.svg';
import dbaas from '@/assets/services/svc/dbaas.svg';
import dbaasSoon from '@/assets/services/svc/dbaas-soon.svg';
import objectStorage from '@/assets/services/svc/object-storage.svg';
import objectStorageSoon from '@/assets/services/svc/object-storage-soon.svg';
import healthcheck from '@/assets/services/svc/healthcheck.svg';
import healthcheckSoon from '@/assets/services/svc/healthcheck-soon.svg';
import organizations from '@/assets/services/svc/organizations.svg';
import organizationsSoon from '@/assets/services/svc/organizations-soon.svg';
import waap from '@/assets/services/svc/waap.svg';
import waapSoon from '@/assets/services/svc/waap-soon.svg';
import waf from '@/assets/services/svc/waf.svg';
import wafSoon from '@/assets/services/svc/waf-soon.svg';
import ssl from '@/assets/services/svc/ssl.svg';
import sslSoon from '@/assets/services/svc/ssl-soon.svg';

/** Provider scope → [active glyph, coming-soon glyph]. */
const ICON_BY_SCOPE: Record<string, [string, string]> = {
  'Internal.FastChannel': [fastchannel, fastchannelSoon],
  'Internal.HLSLowLatency': [livestream, livestreamSoon],
  'External.SwiftCoder': [mediaService, mediaServiceSoon],
  'External.SwiftCoder.LMS': [livestream, livestreamSoon],
  'Internal.Transcoding': [transcoding, transcodingSoon],
  'Internal.Aes': [aesGateway, aesGatewaySoon],
  'External.Mlytics': [multiCdn, multiCdnSoon],
  'Internal.Opennebula': [cloudCompute, cloudComputeSoon],
  'Internal.VMware': [enterpriseCompute, enterpriseComputeSoon],
  'Internal.Functions': [serverless, serverlessSoon],
  'Internal.ContainerRegistry': [containerRegistry, containerRegistrySoon],
  'Internal.Opennebula.Kubernetes': [kubernetes, kubernetesSoon],
  'Internal.Opennebula.Database': [dbaas, dbaasSoon],
  'Internal.Minio': [objectStorage, objectStorageSoon],
  'Internal.Minio.Managed': [objectStorage, objectStorageSoon],
  'Internal.Healthcheck': [healthcheck, healthcheckSoon],
  'Management.Organizations': [organizations, organizationsSoon],
  'Security.WAAP': [waap, waapSoon],
  'Security.WAF': [waf, wafSoon],
  'Security.Certificates': [ssl, sslSoon],
};

/** The product glyph URL for a scope, or undefined when unmapped (caller falls
 *  back to the generic category icon). `coming` picks the dimmed variant. */
export function serviceIconForScope(scope: string | undefined, coming = false): string | undefined {
  if (!scope) return undefined;
  const pair = ICON_BY_SCOPE[scope];
  return pair ? (coming ? pair[1] : pair[0]) : undefined;
}
